import { formatCents, formatDecimal, tt } from '@agos/shared'

/**
 * The external couriers' section of *Nastavenia rozvozu* - `FRONTEND.md` §9.786, on BACKEND.md §10A.438 A, owner
 * O139.
 *
 * The owner's answers: the delivery fee **is** the courier's reward, *base + € per km* (answer 1); *Rozvoz
 * zabezpečujú* own drivers or only external ones (the default announced and accepted); the pickup time counts kitchen
 * pieces (answer 8, *do 5 produktov 10-15 min, nad 5 20 min*). The server stores and validates the section
 * (`ExternalDelivery.patched`), and names the field it refuses; this module reads it into a form and back.
 *
 * **Every figure is a string while it is typed**, so a box can be cleared and retyped - and **a cleared box is
 * refused here by name** rather than sent, because `Number('')` is 0 and the server would store a reward rule that
 * pays nought (`agos-an-empty-string-parses-as-zero`).
 */

export type DeliveryMode = 'own' | 'external'

export interface CourierForm {
  mode: DeliveryMode
  rewardBase: string
  rewardPerKm: string
  rewardMinimum: string
  maxKm: string
  minimumOrder: string
  pickupPiecesThreshold: string
  pickupSmallMinutes: string
  pickupLargeMinutes: string
}

type Field = Exclude<keyof CourierForm, 'mode'>

/** Each box's label - also the name the refusal uses, so the sentence points at the box. */
export const COURIER_FIELD_LABELS: Record<Field, string> = {
  rewardBase: tt('Základ odmeny (€)'),
  rewardPerKm: tt('Za každý km (€)'),
  rewardMinimum: tt('Najnižšia odmena (€)'),
  maxKm: tt('Najďalej (km)'),
  minimumOrder: tt('Minimálna objednávka (€)'),
  pickupPiecesThreshold: tt('Menšia objednávka do (ks z kuchyne)'),
  pickupSmallMinutes: tt('Menšia objednávka pripravená do (min)'),
  pickupLargeMinutes: tt('Väčšia objednávka pripravená do (min)'),
}

const FIELDS = Object.keys(COURIER_FIELD_LABELS) as Field[]
const WHOLE: readonly Field[] = ['pickupPiecesThreshold', 'pickupSmallMinutes', 'pickupLargeMinutes']

function shown(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'string') return value
  return ''
}

/**
 * The section as a form, or null when the server sent none - a backend older than §10A.438, where offering the
 * section would save into nothing. `GET /settings/delivery` fills every field (an absent stored key reads as its
 * default), so a present section is a whole one.
 */
export function courierFormOf(payload: unknown): CourierForm | null {
  if (typeof payload !== 'object' || payload === null) return null
  const section = (payload as Record<string, unknown>).externalDelivery
  if (typeof section !== 'object' || section === null) return null
  const row = section as Record<string, unknown>
  return {
    mode: row.mode === 'external' ? 'external' : 'own',
    rewardBase: shown(row.rewardBase),
    rewardPerKm: shown(row.rewardPerKm),
    rewardMinimum: shown(row.rewardMinimum),
    maxKm: shown(row.maxKm),
    minimumOrder: shown(row.minimumOrder),
    pickupPiecesThreshold: shown(row.pickupPiecesThreshold),
    pickupSmallMinutes: shown(row.pickupSmallMinutes),
    pickupLargeMinutes: shown(row.pickupLargeMinutes),
  }
}

/** A typed figure, with a decimal comma accepted - the way somebody in Slovakia writes 2,50. */
export function figureOf(text: string): number | null {
  const trimmed = text.trim().replace(',', '.')
  if (trimmed === '' || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null
  return Number(trimmed)
}

/**
 * The first box that cannot be saved, named - or null. Only what the server cannot say for itself: a blank (which
 * would arrive as nought) and something that is not a number. The bounds are the server's, and its sentence names
 * the box too.
 */
export function courierFormProblem(form: CourierForm): string | null {
  for (const field of FIELDS) {
    const label = COURIER_FIELD_LABELS[field]
    if (form[field].trim() === '') return `${tt('Vyplňte')}: ${label}`
    const value = figureOf(form[field])
    if (value === null) return `${label} ${tt('musí byť číslo.')}`
    if (WHOLE.includes(field) && !Number.isInteger(value)) return `${label} ${tt('musí byť celé číslo.')}`
  }
  return null
}

/** The section as `PUT /settings/delivery` takes it under `externalDelivery`. Call after `courierFormProblem`. */
export function courierPatchOf(form: CourierForm): Record<string, unknown> {
  const patch: Record<string, unknown> = { mode: form.mode }
  for (const field of FIELDS) patch[field] = figureOf(form[field])
  return patch
}

/**
 * The reward for a distance, in cents - the server's `ExternalDelivery.rewardFor`, line for line: base plus the
 * per-km rate times the distance, rounded half up to ten cents, and never under the minimum.
 *
 * **Integer arithmetic on thousandths of a cent**, because 0,50 € × 3,2 km in floating point is not 1,60 €, and a
 * half-up rounding at exactly 3,05 € is the case where that matters. `courier-reward-cases.json` holds the cases both
 * sides check, so the worked example on the screen and the reward fixed on an order cannot disagree.
 */
export function courierRewardCents(
  rule: { baseCents: number; perKmCents: number; minimumCents: number },
  distanceKm: number,
): number {
  const metres = Math.max(0, Math.round(distanceKm * 1000))
  const thousandths = rule.baseCents * 1000 + rule.perKmCents * metres
  const tenCents = Math.floor((thousandths + 5000) / 10000) * 10
  return Math.max(tenCents, rule.minimumCents)
}

/** The distances the worked example shows - a short, a typical and a long run in a small town. */
export const EXAMPLE_KM: readonly number[] = [2, 3.2, 6]

/** *3,2 km → 4,10 €*, for each example distance, or an empty list while a box cannot be read. */
export function rewardExamples(form: CourierForm): string[] {
  const base = figureOf(form.rewardBase)
  const perKm = figureOf(form.rewardPerKm)
  const minimum = figureOf(form.rewardMinimum)
  if (base === null || perKm === null || minimum === null) return []
  const rule = {
    baseCents: Math.round(base * 100),
    perKmCents: Math.round(perKm * 100),
    minimumCents: Math.round(minimum * 100),
  }
  return EXAMPLE_KM.map(
    (distance) => `${formatDecimal(distance, 1)} km → ${formatCents(courierRewardCents(rule, distance))}`,
  )
}

/** What each mode means, under the switch. */
export function modeNote(mode: DeliveryMode): string {
  return mode === 'external'
    ? tt(
        'Každá objednávka na doručenie ide hneď externým šoférom. Doprava sa počíta z ich odmeny, nie z doručovacích zón.',
      )
    : tt(
        'Rozvážajú vaši šoféri a doprava sa počíta zo zón. Objednávku externým ponúknete na nástenke tlačidlom Ponúknuť externým - pri kanáloch, ktoré to majú zapnuté v Predaj a účtovanie (Ponúka externým).',
      )
}
